import type { SnippetLanguages } from "@twilio-paste/core/syntax-highlighter-library";
import {
  DOMNode as ParserDomNode,
  Element as ParserElement,
} from 'html-react-parser';
import { renderPasteComponentByHtmlTag } from './renderPasteComponentByHtmlTag';
import { renderPasteComponentByClassName } from './renderPasteComponentByClassName';
import { constants, types } from '../definitions';

/**
 * Checks whether the domNode is an Element per html-react-parser
 *  - This is a workaround approach (https://github.com/remarkablemark/html-react-parser/issues/616)
 *    to this bug (https://github.com/remarkablemark/html-react-parser/issues/633)
 *  - Unfortunately, theis specific type issues that devs are running into are not reproducible by the maintainer
 *    so there is no immediate plan on fixing this. But instead, the maintainer has provided the workaround
 *  - @todo - no action needed yet, but if this issue is resolved by the maintainer, then we can remove this and just use
 *            `domeNode instanceof ParserElement`
 */
const isElement = (domNode: ParserDomNode): domNode is ParserElement => {
  const isTag = ['tag', 'script'].includes(domNode.type);
  const hasAttributes = (domNode as ParserElement).attribs !== undefined;

  return isTag && hasAttributes;
};

/**
 * Checks if the current DOM node is a multi-lined code block
 *   - Currrently, ZD wraps <code> with <pre> when displaying a multi-line code
 *   - e.g. <pre><code>function foo() { \n return 'bar'}</code><pre>
 */
const isMultiLineCodeBlock = (
  parentElement: ParserElement['parentNode'],
): boolean => {
  return (
    !!parentElement && isElement(parentElement) && parentElement.name === 'pre'
  );
};

/**
 * Converts className into snippet language (specific to ZD use case)
 *  - ZD provides the code language written within <code> by className
 *  - e.g "javascript", "language-shell", "language-go", etc.,
 *  - The goal is to convert the className into a value:SnippetLanaguages
 *    which will be passed down as `language` props to <CodeBlock />
 */
const convertClassNameToSnippetLanguage = (
  className: string,
): SnippetLanguages => {
  const snippetLanguage = constants.CODE_BLOCK_SNIPPET_LANGUAGES.find(
    (language) => {
      return className.includes(language);
    },
  ) as SnippetLanguages | undefined;

  return snippetLanguage || 'bash';
};

/**
 * Convert dom elements that are wrapped inside the <code> element to string
 *  - The usage of this util function is only for converting ZD htmlstring (do not use for anything else)
 *  - This is a function that handles very small edge cases that came up due to the nature of
 *    how `html-react-parser` library works.
 *  - On high-level, if there exists additional doms inside the <code> element, we want to handle that and return as string
 */
const convertCodeElementsToString = (elements): string => {
  if (typeof elements === 'string') {
    return elements;
  }

  if (elements instanceof Array) {
    const childElements = elements.map((element) => {
      return convertCodeElementsToString(element);
    });
    const childElementsString = childElements.join('');

    return childElementsString;
  }

  if (typeof elements === 'object') {
    const isElementBr = elements?.props?.as === 'br';

    return isElementBr ? '\n' : '';
  }

  return '';
};

// Removes styling attributes(class and style) from the original attributes
const composeAttributesWithoutStyle = (
  attributes: types.Attributes,
): types.Attributes => {
  const { class: className, style, ...filteredAttributes } = attributes;

  return filteredAttributes;
};

export {
  composeAttributesWithoutStyle,
  convertClassNameToSnippetLanguage,
  convertCodeElementsToString,
  renderPasteComponentByHtmlTag,
  renderPasteComponentByClassName,
  isElement,
  isMultiLineCodeBlock,
};
